import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { SCROLL_OFFSET, H2 } from "./sections";

const FAQS = [
  {
    question:
      "Can I use voice prompting AI with ChatGPT, Claude, and Gemini using the same tool?",
    answer:
      "Yes. Zero Typing AI works as an overlay across apps, so the same setup works in ChatGPT, Claude, Gemini, and other AI chat tools without needing separate configuration for each one.",
  },
  {
    question:
      "Why use Zero Typing AI instead of the voice typing already built into ChatGPT, Claude, or Gemini?",
    answer:
      "Built-in voice typing in most AI tools is designed for general dictation or short commands, not for the longer, detailed prompts that get the best results. Zero Typing AI is fine-tuned specifically for voice prompting AI use cases: it adds punctuation, follows natural speech patterns, and cleans up the text so it reads clearly. Try dictating the same prompt with an app's own voice typing and then with Zero Typing AI to compare the results directly.",
  },
  {
    question:
      "Does voice prompting AI produce different results than typing the same prompt?",
    answer:
      "The AI model itself processes the text the same way regardless of how it was entered. What differs is that spoken prompts tend to naturally include more context and detail, which can lead to more specific, useful responses.",
  },
  {
    question:
      "Do I need to switch tools when I move between different AI chatbots?",
    answer:
      "No. Zero Typing AI works the same way across ChatGPT, Claude, Gemini, and other AI chat tools, so you can use one voice prompting AI tool everywhere instead of switching between each app's own built-in voice typing.",
  },
  {
    question: 'Will filler words like "um" end up in my prompt?',
    answer:
      "Zero Typing AI applies AI-powered cleanup to dictated text, removing filler words and false starts so the transcribed prompt reads cleanly rather than as a raw transcript.",
  },
  {
    question: "Can I save a prompt structure I use often?",
    answer:
      "Yes. Zero Typing AI supports custom prompts, letting you set a structure or style that is applied automatically each time you dictate, rather than repeating the same instructions every time.",
  },
  {
    question:
      "Is this different from a voice assistant like Siri or Google Assistant?",
    answer:
      "Yes. Voice assistants are built around short commands and predefined actions. Zero Typing AI is built for open-ended dictation, full sentences and paragraphs, which is what effective AI prompts usually require.",
  },
];

const FAQSection = () => {
  // Single-open accordion; every item starts collapsed.
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="faqs"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className="w-full bg-white font-inter"
    >
      <div className="mx-auto max-w-[1440px] px-6 pb-[64px] md:px-12 md:pb-[88px] lg:px-[101px] lg:pb-[112px]">
        <h2 className={`text-center ${H2}`}>Frequently Asked Questions</h2>

        <p className="mx-auto mt-[12.88px] max-w-[620px] text-center font-inter text-[16px] font-normal leading-[29.6px] tracking-[0px] text-[#0A0D1699]">
          Everything about dictating prompts with Zero Typing AI.
        </p>

        <div className="mx-auto mt-[30.05px] max-w-[820px] border-t-[1.07px] border-[#E6ECF5]">
          {FAQS.map(({ question, answer }, index) => {
            const isOpen = openIndex === index;

            return (
              <div key={question} className="border-b-[1.07px] border-[#E6ECF5]">
                <h3>
                  <button
                    type="button"
                    id={`prompting-faq-trigger-${index}`}
                    aria-expanded={isOpen}
                    aria-controls={`prompting-faq-panel-${index}`}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 py-[18px] text-left sm:py-[21.46px]"
                  >
                    <span className="font-jakarta text-[15px] font-medium leading-[24px] tracking-[0px] text-[#0A0D16] sm:text-[16.1px] sm:leading-[26.16px]">
                      {question}
                    </span>

                    <ChevronDown
                      className={`h-[18px] w-[18px] shrink-0 text-[#3AB1F6] transition-transform duration-300 ${
                        isOpen ? "rotate-180" : "rotate-0"
                      }`}
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                  </button>
                </h3>

                {/*
                  grid-rows 0fr -> 1fr animates to the answer's natural height
                  without measuring it, and keeps the copy in the DOM so it
                  stays indexable while collapsed.
                */}
                <div
                  id={`prompting-faq-panel-${index}`}
                  role="region"
                  aria-labelledby={`prompting-faq-trigger-${index}`}
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="pb-[18px] pr-[34px] font-inter text-[15px] font-normal leading-[26px] tracking-[0px] text-[#0A0D16B2] sm:pb-[21.46px] sm:text-[16px] sm:leading-[29.6px]">
                      {answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
